const PRODUCTION_WEBSITE = 'https://complyedge.io';
const PRODUCTION_TRUST = 'https://trust.complyedge.io';
const PRODUCTION_API = 'https://eu.api.complyedge.io';
const US_API = 'https://api.complyedge.io';

/** Fallback bases when not started via ./run.sh (no VITE_* injected). */
/** Match CE_PORT_*_BASE in scripts/development/local-dev-ports.sh */
const LOCAL_PORT_DEFAULTS = {
  website: 18875,
  trust: 18876,
  dashboard: 18873,
  api: 18800,
};

function localUrl(service: keyof typeof LOCAL_PORT_DEFAULTS): string {
  return `http://localhost:${LOCAL_PORT_DEFAULTS[service]}`;
}

function isLocalHost(): boolean {
  if (import.meta.env.MODE === 'test') {
    return false;
  }
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

function envUrl(name: keyof ImportMetaEnv): string | undefined {
  const value = import.meta.env[name];
  if (typeof value === 'string' && value.trim()) {
    return value.replace(/\/$/, '');
  }
  return undefined;
}

export const getApiUrl = (): string => {
  const envApiUrl = envUrl('VITE_API_URL');
  if (envApiUrl) {
    return envApiUrl;
  }

  if (isLocalHost()) {
    // Vite dev/preview proxy → eu.api.complyedge.io when no local API is running
    return '';
  }

  return PRODUCTION_API;
};

/** Canonical marketing site: badge logos, footer, CTA primary. */
export const getWebsiteUrl = (): string => {
  return envUrl('VITE_WEBSITE_URL') ?? (isLocalHost() ? localUrl('website') : PRODUCTION_WEBSITE);
};

/** Public trust portal base: landing slug hint, cross-links. */
export const getTrustBaseUrl = (): string => {
  return envUrl('VITE_TRUST_URL') ?? (isLocalHost() ? localUrl('trust') : PRODUCTION_TRUST);
};

export const publicTrustUrl = (slug: string): string =>
  `${getApiUrl()}/v1/public/trust/${encodeURIComponent(slug)}`;

/**
 * Session-only preview of the signed-in tenant's own trust data, public or not.
 * Used when the page is opened with `?preview=1` from the dashboard.
 */
export const tenantTrustPreviewUrl = (): string => `${getApiUrl()}/v1/tenant/trust/preview`;

/** Origin of a public API response, when it is one of the two production hosts. */
export function publicApiOrigin(url: string | undefined | null): string | null {
  if (!url) {
    return null;
  }
  try {
    const origin = new URL(url).origin;
    if (origin === PRODUCTION_API || origin === US_API) {
      return origin;
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * When the EU host does not have the slug, ask the US host once.
 * A relative or non-EU URL does not hop. A hit on both is not a loop.
 */
export function usPublicTrustUrl(requestedUrl: string, slug: string): string | null {
  if (publicApiOrigin(requestedUrl) !== PRODUCTION_API) {
    return null;
  }
  return `${US_API}/v1/public/trust/${encodeURIComponent(slug)}`;
}
